import {
  PointerEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react'

/**
 * Turns a vertical pointer drag into delta callbacks.
 * Positive deltas mean the pointer moved up.
 *
 * Spread the returned handlers onto the draggable element.
 */
export function useVerticalDrag(
  onDelta: (deltaPx: number) => void
) {
  const lastY = useRef<number | null>(null)

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      event.preventDefault()
      event.currentTarget.setPointerCapture(event.pointerId)
      lastY.current = event.clientY
    },
    []
  )

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (lastY.current === null) return
      onDelta(lastY.current - event.clientY)
      lastY.current = event.clientY
    },
    [onDelta]
  )

  const onPointerUp = useCallback(() => {
    lastY.current = null
  }, [])

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
  }
}

/**
 * Adjust a value with the mouse wheel.
 *
 * React registers wheel listeners as passive, which blocks
 * preventDefault, so this attaches a non-passive native
 * listener to the referenced element instead.
 */
export function useWheelAdjust(
  ref: RefObject<HTMLElement | null>,
  onStep: (direction: 1 | -1) => void
) {
  const onStepRef = useRef(onStep)

  useEffect(() => {
    onStepRef.current = onStep
  }, [onStep])

  // This wheel listener has [ref] as its dependency.
  // RefObject identity does not change, so this never re-fires
  // even if the element changes. That's correct, but if ref.current
  // is null on mount (e.g. the component conditionally renders),
  // the listener is never attached and there's no recovery.
  // This is a known limitation of useEffect + refs;
  useEffect(() => {
    const element = ref.current
    if (!element) return
    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      onStepRef.current(event.deltaY < 0 ? 1 : -1)
    }
    element.addEventListener('wheel', onWheel, {
      passive: false,
    })
    return () => element.removeEventListener('wheel', onWheel)
  }, [ref])
}
